// Renders the official PayPal Smart Button. Used for one-time checkouts
// and (with intent="subscription" + planId) recurring subscriptions.
import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  capturePaypalOrder,
  createPaypalOrder,
  loadPaypalSdk,
  registerPaypalSubscription,
} from "@/lib/paypal";
import { toast } from "sonner";

type Props = {
  amountCents?: number;
  currency?: string;
  featureKey?: string;
  /** When set, renders a subscription button for this PayPal plan id. */
  planId?: string;
  onSuccess?: (result: { type: "order" | "subscription"; id: string }) => void;
  onError?: (err: Error) => void;
};

export const PayPalButton = ({
  amountCents,
  currency = "USD",
  featureKey,
  planId,
  onSuccess,
  onError,
}: Props) => {
  const container = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let buttons: ReturnType<typeof window.paypal.Buttons> | null = null;

    (async () => {
      try {
        const paypal = await loadPaypalSdk({
          intent: planId ? "subscription" : "capture",
          currency,
          vault: !!planId,
        });
        if (cancelled || !container.current) return;

        const config: Record<string, unknown> = {
          style: { layout: "vertical", color: "gold", shape: "pill", label: planId ? "subscribe" : "paypal" },
          onError: (err: unknown) => {
            const e = err instanceof Error ? err : new Error(String(err));
            toast.error(e.message || "PayPal error");
            onError?.(e);
          },
        };

        if (planId) {
          config.createSubscription = (_: unknown, actions: { subscription: { create: (o: { plan_id: string }) => Promise<string> } }) =>
            actions.subscription.create({ plan_id: planId });
          config.onApprove = async (data: { subscriptionID: string }) => {
            try {
              await registerPaypalSubscription(data.subscriptionID, planId);
              toast.success("Subscription activated");
              onSuccess?.({ type: "subscription", id: data.subscriptionID });
            } catch (e) {
              toast.error((e as Error).message || "Failed to register subscription");
            }
          };
        } else {
          if (!amountCents || amountCents <= 0) {
            setErrMsg("Invalid amount");
            setLoading(false);
            return;
          }
          config.createOrder = async () =>
            await createPaypalOrder({ amountCents, currency, featureKey });
          config.onApprove = async (data: { orderID: string }) => {
            try {
              const r = await capturePaypalOrder(data.orderID);
              if (r.status === "COMPLETED") {
                toast.success("Payment completed");
                onSuccess?.({ type: "order", id: data.orderID });
              } else {
                toast.message(`PayPal status: ${r.status}`);
              }
            } catch (e) {
              toast.error((e as Error).message || "Capture failed");
            }
          };
        }

        buttons = paypal.Buttons(config);
        if (!buttons.isEligible()) {
          setErrMsg("PayPal is not available in your browser");
          setLoading(false);
          return;
        }
        await buttons.render(container.current);
        if (!cancelled) setLoading(false);
      } catch (e) {
        if (cancelled) return;
        setErrMsg((e as Error).message || "Failed to load PayPal");
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      try { buttons?.close(); } catch { /* noop */ }
    };
  }, [amountCents, currency, featureKey, planId, onError, onSuccess]);

  return (
    <div className="w-full">
      {loading && (
        <div className="flex items-center justify-center gap-2 py-4 text-xs text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading PayPal…
        </div>
      )}
      {errMsg && <p className="text-xs text-destructive text-center py-2">{errMsg}</p>}
      <div ref={container} />
    </div>
  );
};
